// packages/front/src/portfolio/portfolio-switcher.tsx
//
// PortfolioSwitcher — dropdown for selecting the active portfolio.
// portfolioId is a view-state selection that drives all server-side queries.

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiFetch, qk, Select } from "../_common";
import { useMutation } from "@tanstack/react-query";
import { usePortfolios } from "./portfolio.hooks";
import type { Portfolio } from "./portfolio.types";

interface PortfolioSwitcherProps {
  portfolioId: string;
  onPortfolioChange: (portfolioId: string) => void;
}

export function PortfolioSwitcher({
  portfolioId,
  onPortfolioChange,
}: PortfolioSwitcherProps) {
  const { data, isLoading } = usePortfolios();
  const portfolios = data?.items ?? [];
  const [isOpen, setIsOpen] = useState(false);

  const current = portfolios.find((p) => p.id === portfolioId);

  return (
    <div className="relative inline-block">
      <button
        type="button"
        className="flex items-center gap-2 rounded border border-gray-300 px-3 py-1.5 text-sm font-medium hover:bg-gray-50"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
      >
        <span>{current?.name ?? "Select portfolio"}</span>
        <span className="text-gray-400">▼</span>
      </button>
      {isOpen && (
        <div className="absolute left-0 z-50 mt-1 min-w-[200px] rounded border border-gray-200 bg-white text-gray-900 shadow-lg">
          {portfolios.map((p: Portfolio) => (
            <button
              key={p.id}
              type="button"
              className={`flex w-full items-center justify-between px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 hover:text-gray-900 ${
                p.id === portfolioId ? "bg-blue-50 font-medium" : ""
              }`}
              onClick={() => {
                onPortfolioChange(p.id);
                setIsOpen(false);
              }}
            >
              <span>{p.name}</span>
              {p.displayCurrency && (
                <span className="text-xs text-gray-400">{p.displayCurrency}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Create Portfolio Modal ─────────────────────────────────────────────────

interface CreatePortfolioModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (portfolio: Portfolio) => void;
}

export function CreatePortfolioModal({
  open,
  onClose,
  onCreated,
}: CreatePortfolioModalProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [displayCurrency, setDisplayCurrency] = useState("USD");

  const createMutation = useMutation({
    mutationFn: (body: { name: string; description?: string | undefined; displayCurrency: string }) =>
      apiFetch<Portfolio>("/api/portfolio/portfolios", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: (portfolio) => {
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.portfolios });
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.all });
      onCreated?.(portfolio);
      setName("");
      setDescription("");
      setDisplayCurrency("USD");
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) return;
    createMutation.mutate({
      name,
      description: description || undefined,
      displayCurrency,
    });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
        <h2 className="mb-4 text-lg font-semibold">Create Portfolio</h2>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div>
            <label className="text-sm font-medium">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
              placeholder="My Portfolio"
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium">Description (optional)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
              placeholder="Description"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Display Currency</label>
            <Select
              value={displayCurrency}
              onChange={setDisplayCurrency}
              className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
              options={[
                { value: "USD", label: "USD ($)" },
                { value: "RUB", label: "RUB (₽)" },
                { value: "EUR", label: "EUR (€)" },
                { value: "CNH", label: "CNH (¥)" },
              ]}
              ariaLabel="Display currency"
            />
          </div>
          {createMutation.error && (
            <div className="text-sm text-red-600">
              {String(createMutation.error)}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-gray-300 px-4 py-1.5 text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || !name}
              className="rounded bg-blue-600 px-4 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {createMutation.isPending ? "Creating..." : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
